import { useMemo } from "react";
import { useSafeFetch } from "../../lib/useSafeFetch";
import { COUNTRIES } from "../public-book/geo";

/**
 * CheckoutFields — dynamic form renderer for the customer booking page.
 *
 * Reads `GET /settings/public#checkout_fields` and:
 *   • Hides fields marked "off".
 *   • Renders "optional" and "required" fields; the `required` HTML
 *     attribute is set only when status === "required".
 *   • Airport / Port / Accommodation groups are shown only when the
 *     pickup or return location has that `location_type`.
 *
 * The parent owns `form` state — we're only rendering inputs against it.
 *
 * v4.0.2 — Added `hideBasics` (skip full_name/email/phone only, so the
 * admin modals which render those themselves can still surface the
 * dynamic Address / Country / City / ZIP / Notes group) and
 * `hideDriver` (skip the id_passport/license_number/driver_age block
 * for the same reason). Country renders as a `<select>` reusing the
 * public COUNTRIES catalogue.
 *
 * v4.0.3 — Added `renderGroup` prop:
 *   • "customer"    → render ONLY the customer group (Address / Country /
 *                     City / ZIP / Notes) with the redundant group header
 *                     suppressed so the parent can host it inline inside
 *                     its own "Customer" card.
 *   • "conditional" → render ONLY airport / port / hotel groups (used
 *                     below the Rental block for location-conditional
 *                     details).
 *   • undefined     → legacy behaviour (all groups the caller allows).
 */
const FIELD_META = {
  full_name:             { group: "customer", label: "Full name",              col: 1 },
  email:                 { group: "customer", label: "Email",                  col: 1, type: "email" },
  phone:                 { group: "customer", label: "Phone",                  col: 1, type: "tel" },
  address:               { group: "customer", label: "Address",                col: 2 },
  country:               { group: "customer", label: "Country",                col: 1, type: "country" },
  city:                  { group: "customer", label: "City",                   col: 1 },
  zip:                   { group: "customer", label: "Zip / Postal code",      col: 1 },
  notes:                 { group: "customer", label: "Notes",                  col: 2, type: "textarea" },
  id_passport:           { group: "driver",   label: "ID / Passport #",        col: 1 },
  license_number:        { group: "driver",   label: "Driver's licence #",     col: 1 },
  driver_age:            { group: "driver",   label: "Driver's age",           col: 1, type: "number" },
  airline_company:       { group: "airport",  label: "Airline / carrier",      col: 1 },
  flight_number:         { group: "airport",  label: "Flight number",          col: 1 },
  flight_time:           { group: "airport",  label: "Flight time",            col: 1, type: "time" },
  vessel_company:        { group: "port",     label: "Cruise line / operator", col: 1 },
  vessel_name:           { group: "port",     label: "Vessel name",            col: 1 },
  vessel_arrival_time:   { group: "port",     label: "Arrival time",           col: 1, type: "time" },
  accommodation_name:    { group: "hotel",    label: "Hotel / villa / apt.",   col: 1 },
  accommodation_address: { group: "hotel",    label: "Accommodation address",  col: 2 },
};

// v4.0.2 — basics also includes `notes` so the admin manual/edit modals
// (which render a "Notes (internal)" textarea themselves) don't show it
// twice via the dynamic renderer.
// v4.0.3 — `notes` removed so the dynamic Notes field lives INSIDE the
// Customer card in both admin modals; the standalone "Notes (internal)"
// textarea has been retired from the Payment section.
const BASIC_CUSTOMER_KEYS = new Set(["full_name", "email", "phone"]);

const GROUP_LABELS = {
  customer: "Customer address & notes",
  driver:   "Driver identity",
  airport:  "Airport pickup — flight details",
  port:     "Port pickup — cruise details",
  hotel:    "Accommodation delivery — venue details",
};

export default function CheckoutFields({
  form, setForm, locations = [],
  hideCustomer = false, hideBasics = false, hideDriver = false,
  renderGroup, // v4.0.3 · "customer" | "conditional" | undefined
  errors = {}, // v4.2.6 · { field_key: "This field is required" }
}) {
  const { data: settings } = useSafeFetch("/settings/public", {
    defaultValue: null,
  });
  const cfg = settings?.checkout_fields || {};

  // Which conditional groups are in scope for this booking?
  const activeGroups = useMemo(() => {
    const names = new Set([form.pickup_location, form.return_location]);
    const groups = new Set(["customer"]);
    for (const loc of locations) {
      if (!names.has(loc.name)) continue;
      const t = (loc.location_type || "").toLowerCase();
      if (t === "airport") groups.add("airport");
      if (t === "port")    groups.add("port");
      if (t === "hotel")   groups.add("hotel");
    }
    return groups;
  }, [form.pickup_location, form.return_location, locations]);

  const renderInput = (key) => {
    const meta = FIELD_META[key];
    const status = cfg[key] || "off";
    if (status === "off") return null;
    if (!activeGroups.has(meta.group)) return null;
    const isRequired = status === "required";
    const value = form[key] ?? "";
    const onChange = (e) => setForm({ ...form, [key]: e.target.value });
    const testId = `checkout-${key.replace(/_/g, "-")}`;
    // v4.2.6 — the red state clears itself the moment a value is entered,
    // so the parent never has to reset its error map on every keystroke.
    const errorText = errors[key] && !String(value).trim() ? errors[key] : "";
    const errorClass = errorText
      ? " border-red-500 focus:ring-red-500 bg-red-50/20"
      : "";

    let inner;
    if (meta.type === "textarea") {
      inner = (
        <textarea
          rows={3} value={value} onChange={onChange}
          required={isRequired}
          className={`editorial-input w-full resize-y text-sm font-normal${errorClass}`}
          data-testid={testId}
        />
      );
    } else if (meta.type === "country") {
      inner = (
        <select
          value={value} onChange={onChange}
          required={isRequired}
          className={`editorial-input w-full text-sm font-normal${errorClass}`}
          data-testid={testId}
        >
          <option value="">Select country…</option>
          {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      );
    } else {
      inner = (
        <input
          type={meta.type || "text"}
          value={value}
          onChange={onChange}
          required={isRequired}
          className={`editorial-input w-full text-sm font-normal${errorClass}`}
          data-testid={testId}
        />
      );
    }

    return (
      <div key={key} className={meta.col === 2 ? "col-span-2" : "col-span-1"}>
        <label className="block text-xs uppercase tracking-[0.2em] text-neutral-500 mb-1">
          {meta.label}
          {isRequired && <span className="text-red-500 font-bold ml-0.5" aria-hidden="true">*</span>}
        </label>
        {inner}
        {errorText && (
          <p className="text-xs text-red-500 mt-1" data-testid={`${testId}-error`}>
            {errorText}
          </p>
        )}
      </div>
    );
  };

  const byGroup = { customer: [], driver: [], airport: [], port: [], hotel: [] };
  for (const [key, meta] of Object.entries(FIELD_META)) {
    if (!activeGroups.has(meta.group)) continue;
    if ((cfg[key] || "off") === "off") continue;
    // v4.0.2 — skip basics (name/email/phone) when caller already
    // renders them (admin manual/edit booking modals).
    if (hideBasics && BASIC_CUSTOMER_KEYS.has(key)) continue;
    byGroup[meta.group].push(key);
  }

  return (
    <div className="space-y-6" data-testid="checkout-fields">
      {["customer", "driver", "airport", "port", "hotel"].map((g) => {
        if (byGroup[g].length === 0) return null;
        if (hideCustomer && (g === "customer" || g === "driver")) return null;
        if (hideDriver && g === "driver") return null;
        // v4.0.3 — `renderGroup` gate: parent decides which groups to
        // mount (customer inside the Customer card, conditional below
        // Rental).
        if (renderGroup === "customer" && g !== "customer") return null;
        if (renderGroup === "conditional" && g === "customer") return null;
        // Conditional groups (airport / port / hotel) render inside a
        // self-contained white card that matches the admin section
        // styling — dedicated container below the Rental block.
        const isConditional = g === "airport" || g === "port" || g === "hotel";
        const containerClass = isConditional
          ? "bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded p-5"
          : "";
        // v4.0.3 — Suppress the "Customer address & notes" header when
        // the customer group is being embedded inside the parent's
        // Customer card (parent already provides the heading).
        const showHeader =
          (g !== "customer" || hideBasics) && renderGroup !== "customer";
        return (
          <div
            key={g}
            className={containerClass}
            data-testid={`checkout-group-${g}`}
          >
            {showHeader && (
              <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 dark:text-neutral-400 mb-3">
                {GROUP_LABELS[g]}
              </p>
            )}
            <div className="grid grid-cols-2 gap-4">
              {byGroup[g].map(renderInput)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
